<script setup lang="ts">
import type { WatchStopHandle } from 'vue';
import { debounce } from 'lodash-es';
import { computed, ref, watch } from 'vue';

import type { IFormSelectInputOption } from '@modules/forms/types';
import type { IPostbackUriOption } from '@modules/postbacks/submodules/edit/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import { EAasmState } from '@modules/core/enums';
import VFormCheckbox from '@modules/forms/components/inputs/VFormCheckbox/VFormCheckbox.vue';
import VFormSelectInput from '@modules/forms/components/inputs/VFormSelectInput/VFormSelectInput.vue';
import VFormTextarea from '@modules/forms/components/inputs/VFormTextarea/VFormTextarea.vue';
import VFormTextInput from '@modules/forms/components/inputs/VFormTextInput/VFormTextInput.vue';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS } from '@modules/forms/constants';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_POSTBACKS } from '@modules/postbacks/constants';
import { EWebhookUriOptionFields } from '@modules/postbacks/submodules/edit/types';
import { useUserSupervision } from '@modules/user/composables';

import type { PostbackUrlBuilderEmits, PostbackUrlBuilderProps } from './types';

const props = defineProps<PostbackUrlBuilderProps>();
const emit = defineEmits<PostbackUrlBuilderEmits>();

const { getTPath } = useI18n();
const { isSupervision } = useUserSupervision();

const uriOptionsInner = ref<IPostbackUriOption[]>();

const optionFieldIsFocused = ref(false);
const customOptionsReadonly = ref(false);
let blurWatcher: WatchStopHandle;

const sourceUrlForGeneration = ref<string | undefined>();
const sourceUrlError = ref<string[] | undefined>();

const methodOptions = computed<IFormSelectInputOption<string>[]>(() => (
  (props.postbackData?.request_methods ?? []).map((item) => ({
    value: item,
    label: item.toUpperCase(),
  }))
));

const isFormDisabled = computed(() => isSupervision.value || props.isSavingUriOption);

function onSourceUrlInput(url?: string): void {
  validateSourceUrl(url);
  sourceUrlForGeneration.value = url;
}

function onSourceUrlBlur(url?: string): void {
  validateSourceUrl(url);
  sourceUrlForGeneration.value = url;
}

function validateSourceUrl(url?: string): URL | undefined {
  let urlObj: URL;
  const errorTPath = getTPath(TRANSLATION_GROUP_POSTBACKS, 'URL_ERROR_SOURCE_URL_INVALID');
  if (!url)
    return undefined;
  try {
    urlObj = new URL(url);
    if (url && urlObj.origin !== 'null') {
      sourceUrlError.value = undefined;
      return urlObj;
    }

    sourceUrlError.value = [errorTPath];
    return undefined;
  } catch {
    sourceUrlError.value = [errorTPath];
    return undefined;
  }
}

function extractValuesFromSourceUrl(): void {
  if (!sourceUrlForGeneration.value)
    return;
  const url = validateSourceUrl(sourceUrlForGeneration.value);
  if (url) {
    emit('url-to-extract-change', url);
    sourceUrlForGeneration.value = undefined;
  }
}

function onChangeRequestOption(request_method?: string): void {
  if (props.postbackData) {
    emit('change-webhook', {
      ...props.postbackData.webhook,
      request_method,
    });
  }
}

function onUrlInput(uri?: string): void {
  if (props.postbackData) {
    emit('change-webhook', {
      ...props.postbackData.webhook,
      uri,
    });
  }
}

const debouncedInput = debounce(onUrlInput, FORM_INPUT_DEBOUNCE_DEFAULT_MS);

function onUrlBlur(): void {
  debouncedInput.flush();
}

function sortUriOptions(options: IPostbackUriOption[]): IPostbackUriOption[] {
  return options.sort((a, b) => {
    if (a.aasm_state === EAasmState.DEFAULT && b.aasm_state !== EAasmState.DEFAULT) {
      return -1;
    }

    if (b.aasm_state === EAasmState.DEFAULT && a.aasm_state !== EAasmState.DEFAULT) {
      return 1;
    }

    if (a.aasm_state === EAasmState.CUSTOM && b.aasm_state === EAasmState.CUSTOM) {
      return Number(a.id) - Number(b.id);
    }

    return a.value?.localeCompare(b.value || '') || 0;
  });
}

const urlOptions = computed<IPostbackUriOption[]>(() => sortUriOptions([
  ...props.uriOptions,
]));

watch([urlOptions, (): boolean => props.isSavingUriOption], ([, newIsSaving]) => {
  const syncForm = (): void => {
    uriOptionsInner.value = urlOptions.value;
  };

  if (!newIsSaving && !optionFieldIsFocused.value) {
    syncForm();
  } else {
    if (blurWatcher) {
      blurWatcher();
    }
    blurWatcher = watch(optionFieldIsFocused, (newValue) => {
      if (!newValue) {
        syncForm();
        blurWatcher();
      }
    });
  }

  customOptionsReadonly.value = newIsSaving;
}, { immediate: true });

function isUrlOptionRemovable(option: IPostbackUriOption): boolean {
  return !!(option.aasm_state !== EAasmState.DEFAULT && option.id && !props.postbackData?.webhook.build);
}

const hasRemovableUrlOptions = computed(() => urlOptions.value?.some((option) => isUrlOptionRemovable(option)));

const finalUrl = computed(() => {
  if (!props.postbackData?.webhook.uri) {
    return undefined;
  }

  const uri = `${props.postbackData.webhook.uri}`;

  let optionsUrl = '';
  for (const item of urlOptions.value) {
    if (item.key && item.active) {
      if (!optionsUrl) {
        optionsUrl += `?${item.key}=${item.value}`;
      } else {
        optionsUrl += `&${item.key}=${item.value}`;
      }
    }
  }

  return `${uri}${optionsUrl}`;
});

function emitChangeUriOption(option: IPostbackUriOption): void {
  emit('change-uri-option', option);
}

function onActiveChanged(option: IPostbackUriOption, value?: boolean): void {
  emitChangeUriOption({
    ...option,
    active: !!value,
  });
}

function emitOptionFieldsChange(option: IPostbackUriOption, field: EWebhookUriOptionFields, value?: string): void {
  emitChangeUriOption({
    ...option,
    [field]: value,
  });
}

const postponedOptionChange = debounce(emitOptionFieldsChange, FORM_INPUT_DEBOUNCE_DEFAULT_MS);

function optionChangeInput(option: IPostbackUriOption, index: number, field: EWebhookUriOptionFields, value?: string): void {
  if (uriOptionsInner.value) {
    uriOptionsInner.value[index] = { ...uriOptionsInner.value[index], [field]: value };
  }
  postponedOptionChange(option, field, value);
}

function onOptionFieldBlur(): void {
  optionFieldIsFocused.value = false;
  postponedOptionChange.flush();
}

function onOptionFieldFocus(): void {
  optionFieldIsFocused.value = true;
}
</script>

<template>
  <div v-data-qa-auto-id="'PostbackUrlBuilder'">
    <div class="flex items-stretch border border-b-0 max-sm:flex-col-reverse">
      <div class="flex items-center border-r p-4 max-sm:w-full max-sm:pt-0 sm:w-[200px]">
        <VButton
          class="w-full"
          :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_SOURCE_URL_GENERATE')"
          :disabled="!!sourceUrlError || !sourceUrlForGeneration"
          @click="extractValuesFromSourceUrl"
        />
      </div>
      <div class="w-full p-4 pb-1 sm:flex-1">
        <VFormTextarea
          :errors="sourceUrlError"
          :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_SOURCE_URL')"
          is-full-width
          :value="sourceUrlForGeneration"
          :readonly="isSupervision || isSavingUriOption"
          @input="onSourceUrlInput"
          @blur="onSourceUrlBlur"
        />
      </div>
    </div>
    <div class="flex items-center border max-sm:flex-col">
      <div class="border-r p-4 pb-1 max-sm:w-full sm:w-[200px]">
        <VFormSelectInput
          :options="methodOptions"
          :errors="errors?.request_method"
          :value="postbackData?.webhook.request_method"
          :label="!postbackData?.webhook.request_method ? $getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_REQUEST_METHOD') : undefined"
          :disabled="isFormDisabled"
          is-full-width
          @change="onChangeRequestOption"
        />
      </div>
      <div class="w-full p-4 pb-1 max-sm:pt-0 sm:flex-1">
        <VFormTextInput
          :errors="errors?.uri"
          :label="!postbackData?.webhook.uri ? $getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_URL') : undefined"
          is-full-width
          :value="postbackData?.webhook.uri"
          :readonly="isFormDisabled"
          @input="debouncedInput"
          @blur="onUrlBlur"
        />
      </div>
    </div>
    <div class="border-x p-4">
      <VFormTextInput
        :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_RESULT_URL')"
        hide-errors
        is-full-width
        :value="finalUrl"
        readonly
      />
    </div>
    <table class="block overflow-x-auto whitespace-nowrap border-r sm:w-full">
      <caption class="h-0 overflow-hidden">
        Postback url-builder
      </caption>
      <thead>
        <tr>
          <th class="w-[50px] border p-4" />
          <th class="border p-4">
            {{ $getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_TABLE_HEADER_KEY') }}
          </th>
          <th class="border p-4">
            {{ $getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_TABLE_HEADER_VALUE') }}
          </th>
          <th class="border border-r-0 p-4">
            {{ $getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_TABLE_HEADER_DESCRIPTION') }}
          </th>
          <th
            v-if="hasRemovableUrlOptions"
            class="w-[50px] border-y p-4"
          />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(option, index) in urlOptions"
          :key="index"
        >
          <td class="w-[50px] border p-4 pb-1">
            <VFormCheckbox
              :disabled="postbackData?.webhook.build || !option.id || isFormDisabled"
              :value="option.active"
              @change="onActiveChanged(option, $event)"
            />
          </td>
          <td class="min-w-[200px] border p-4 pb-1">
            <VFormTextInput
              :value="uriOptionsInner?.[index]?.key"
              :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_TABLE_BODY_KEY')"
              :disabled="postbackData?.webhook.build"
              :readonly="(!option?.id && customOptionsReadonly) || isFormDisabled"
              :errors="option.errors"
              @input="optionChangeInput(option, index, EWebhookUriOptionFields.KEY, $event)"
              @focus="onOptionFieldFocus"
              @blur="onOptionFieldBlur"
            />
          </td>
          <td class="min-w-[200px] border p-4 pb-1">
            <VFormTextInput
              :value="uriOptionsInner?.[index]?.value"
              :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_TABLE_BODY_VALUE')"
              :readonly="(postbackData?.webhook.build || option.aasm_state === EAasmState.DEFAULT) || (!option?.id && customOptionsReadonly) || isFormDisabled"
              @input="optionChangeInput(option, index, EWebhookUriOptionFields.VALUE, $event)"
              @focus="onOptionFieldFocus"
              @blur="onOptionFieldBlur"
            />
          </td>
          <td class="min-w-[200px] border border-r-0 p-4 pb-1 sm:w-full">
            <VFormTextInput
              is-full-width
              :value="option.aasm_state === EAasmState.DEFAULT && option.description ? $t(option.description) : option.description"
              :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_TABLE_BODY_DESCRIPTION')"
              :readonly="postbackData?.webhook.build || option.aasm_state === EAasmState.DEFAULT || isFormDisabled"
              @input="optionChangeInput(option, index, EWebhookUriOptionFields.DESCRIPTION, $event)"
              @focus="onOptionFieldFocus"
              @blur="onOptionFieldBlur"
            />
          </td>
          <td
            v-if="hasRemovableUrlOptions"
            class="w-[50px] border-y p-4"
          >
            <VIconButton
              v-if="isUrlOptionRemovable(option)"
              icon="pi-trash"
              @click="emit('remove-uri-option', option)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
